import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, homePathFor } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import api from "../lib/api";
import { Stethoscope, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { LegalDisclaimer } from "../components/LegalDisclaimer";
import { toast } from "sonner";

function AuthShell({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-12">
        <Link to="/" className="mb-8 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-700 text-white">
            <Stethoscope className="h-5 w-5" />
          </div>
          <span className="font-heading text-lg font-800 text-slate-900">Agenda-ISF-COMO</span>
        </Link>
        {children}
      </div>
      <div className="mx-auto w-full max-w-md px-4 pb-8">
        <LegalDisclaimer variant="bar" />
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Accesso effettuato");
      navigate(homePathFor(user));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="rounded-2xl border border-slate-200 bg-white p-8">
        <h1 className="font-heading text-2xl font-800 text-slate-900">Accedi</h1>
        <p className="mt-1 text-sm text-slate-500">Inserisci le tue credenziali per continuare.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required className="mt-1.5" data-testid="login-email-input" autoComplete="email" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/password-dimenticata" className="text-xs font-500 text-blue-700 hover:underline" data-testid="forgot-password-link">
                Password dimenticata?
              </Link>
            </div>
            <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)}
              required className="mt-1.5" data-testid="login-password-input" autoComplete="current-password" />
          </div>
          {error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="login-error">{error}</p>
          )}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-blue-700 hover:bg-blue-800" data-testid="login-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-500">
          Non hai un account?{" "}
          <Link to="/registrati" className="font-600 text-blue-700 hover:underline" data-testid="goto-register-link">
            Registrati
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}

export { AuthShell };
